import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type MouseEvent as ReactMouseEvent,
} from 'react'
import { createPortal } from 'react-dom'
import { Tooltip } from 'antd'
import {
  BorderOutlined,
  CloseOutlined,
  ExportOutlined,
  MinusOutlined,
  ReloadOutlined,
  SnippetsOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { toast } from '@crosscode/gravity-components'
import { GuacamoleSession, type GuacamoleSessionHandle } from './GuacamoleSession'
import { disposeGuacamoleSession } from './guacamoleLifecycle'
import {
  canOpenEmbedded,
  resolveTunnelUrl,
  type OpenRemoteOptions,
} from './types'
import styles from './GuacamoleSession.module.css'

export interface RemoteSession {
  id: string
  title: string
  type: string
  tunnelUrl: string
  connectionToken: string
  url?: string
}

interface RemoteContextValue {
  openRemote: (opts: OpenRemoteOptions) => void
  closeRemote: () => void
}

const RemoteContext = createContext<RemoteContextValue | null>(null)

const TITLEBAR_H = 40
const MARGIN = 24
const MIN_VISIBLE = 72

const tipProps = {
  mouseEnterDelay: 0.2,
  mouseLeaveDelay: 0.05,
  placement: 'bottom' as const,
  getPopupContainer: () => document.body,
  zIndex: 1400,
}

function fitWindow(remoteW: number, remoteH: number) {
  const maxW = Math.max(260, window.innerWidth - MARGIN * 2)
  const maxStageH = Math.max(200, window.innerHeight - MARGIN * 2 - TITLEBAR_H)
  const scale = Math.min(maxW / remoteW, maxStageH / remoteH)
  return {
    width: Math.round(Math.max(260, remoteW * scale)),
    stageHeight: Math.round(Math.max(200, remoteH * scale)),
  }
}

function clampPos(x: number, y: number, winW: number) {
  const minX = -(winW - MIN_VISIBLE)
  const maxX = window.innerWidth - MIN_VISIBLE
  const maxY = Math.max(0, window.innerHeight - TITLEBAR_H)
  return {
    x: Math.max(minX, Math.min(x, maxX)),
    y: Math.max(0, Math.min(y, maxY)),
  }
}

function centerPos(winW: number, stageH: number) {
  const totalH = TITLEBAR_H + stageH
  return clampPos(
    Math.round((window.innerWidth - winW) / 2),
    Math.round((window.innerHeight - totalH) / 2),
    winW,
  )
}

export function useRemoteDesktop() {
  const ctx = useContext(RemoteContext)
  if (!ctx) throw new Error('useRemoteDesktop must be used within RemoteDesktopHost')
  return ctx
}

export function RemoteDesktopHost({ children }: { children?: ReactNode }) {
  const [session, setSession] = useState<RemoteSession | null>(null)
  const [sessionKey, setSessionKey] = useState(0)
  const [statusLabel, setStatusLabel] = useState('Подключение…')
  const [winWidth, setWinWidth] = useState(420)
  const [stageHeight, setStageHeight] = useState(640)
  const [pos, setPos] = useState({ x: 0, y: 0 })
  const [minimized, setMinimized] = useState(false)
  const guacRef = useRef<GuacamoleSessionHandle>(null)
  const sessionRef = useRef<RemoteSession | null>(null)
  const lastSize = useRef({ w: 0, h: 0 })
  const dragRef = useRef<{ ox: number; oy: number; sx: number; sy: number } | null>(null)
  const openingRef = useRef(false)
  const userMovedRef = useRef(false)
  const winWidthRef = useRef(winWidth)
  const minimizedRef = useRef(minimized)

  sessionRef.current = session
  winWidthRef.current = winWidth
  minimizedRef.current = minimized

  const tearDown = useCallback(() => {
    try {
      guacRef.current?.disconnect()
    } catch {
      /* ignore */
    }
    disposeGuacamoleSession()
  }, [])

  const closeRemote = useCallback(() => {
    tearDown()
    setSession(null)
    setStatusLabel('Отключено')
    setMinimized(false)
    lastSize.current = { w: 0, h: 0 }
    dragRef.current = null
    openingRef.current = false
    userMovedRef.current = false
  }, [tearDown])

  const openRemote = useCallback(
    (opts: OpenRemoteOptions) => {
      if (openingRef.current) return
      if (!canOpenEmbedded(opts)) {
        if (opts.url) {
          toast.warning('Нет токена встраивания — открываю UI Guacamole')
          window.open(opts.url, '_blank', 'noopener,noreferrer')
          return
        }
        toast.error('Нет connectionToken для Guacamole')
        return
      }

      openingRef.current = true
      tearDown()

      const type = (opts.type || 'RDP').toUpperCase()
      setStatusLabel('Подключение…')
      setSessionKey((k) => k + 1)
      lastSize.current = { w: 0, h: 0 }
      setMinimized(false)
      userMovedRef.current = false

      let nextW = 420
      let nextH = 640
      if (type === 'SSH') {
        nextW = 820
        nextH = 480
      } else if (type === 'VNC') {
        nextW = 400
        nextH = Math.min(window.innerHeight - MARGIN * 2 - TITLEBAR_H, 700)
      } else {
        nextW = Math.min(window.innerWidth - MARGIN * 2, 1040)
        nextH = Math.min(window.innerHeight - MARGIN * 2 - TITLEBAR_H, 640)
      }

      setWinWidth(nextW)
      setStageHeight(nextH)
      setPos(centerPos(nextW, nextH))

      setSession({
        id: `guac-${Date.now()}`,
        title: opts.title,
        type,
        tunnelUrl: resolveTunnelUrl(opts.tunnelUrl),
        connectionToken: String(opts.connectionToken),
        url: opts.url,
      })

      window.setTimeout(() => {
        openingRef.current = false
      }, 400)
    },
    [tearDown],
  )

  const retry = useCallback(() => {
    const cur = sessionRef.current
    if (!cur) return
    tearDown()
    setStatusLabel('Переподключение…')
    setSessionKey((k) => k + 1)
  }, [tearDown])

  const pasteClipboard = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText()
      if (!text) {
        toast.info('Буфер обмена пуст')
        return
      }
      guacRef.current?.sendClipboard(text)
      toast.success('Буфер отправлен в сессию')
    } catch {
      toast.error('Нет доступа к буферу обмена браузера')
    }
  }, [])

  const onContentSize = useCallback((w: number, h: number) => {
    if (!w || !h) return
    if (lastSize.current.w === w && lastSize.current.h === h) return
    lastSize.current = { w, h }
    const fit = fitWindow(w, h)
    setWinWidth(fit.width)
    setStageHeight(fit.stageHeight)
    if (userMovedRef.current) {
      setPos((prev) => clampPos(prev.x, prev.y, fit.width))
    } else {
      setPos(centerPos(fit.width, fit.stageHeight))
    }
  }, [])

  useEffect(() => {
    if (!session) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        closeRemote()
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => {
      window.removeEventListener('keydown', onKey, true)
    }
  }, [session, closeRemote])

  useEffect(() => {
    return () => {
      try {
        guacRef.current?.disconnect()
      } catch {
        /* ignore */
      }
    }
  }, [])

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const d = dragRef.current
      if (!d) return
      const w = minimizedRef.current
        ? Math.min(winWidthRef.current, 320)
        : winWidthRef.current
      const next = clampPos(d.sx + (e.clientX - d.ox), d.sy + (e.clientY - d.oy), w)
      if (next.x !== d.sx || next.y !== d.sy) {
        userMovedRef.current = true
      }
      setPos(next)
    }
    const onUp = () => {
      dragRef.current = null
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [])

  const startDrag = (e: ReactMouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) return
    e.preventDefault()
    dragRef.current = {
      ox: e.clientX,
      oy: e.clientY,
      sx: pos.x,
      sy: pos.y,
    }
  }

  const toggleMinimized = () => setMinimized((v) => !v)

  const connected = statusLabel === 'Подключено'
  const value = useMemo(() => ({ openRemote, closeRemote }), [openRemote, closeRemote])
  const displayWidth = minimized ? Math.min(winWidth, 320) : winWidth

  const overlay =
    session &&
    createPortal(
      <div className={styles.layer} role="presentation">
        <div
          className={`${styles.window} ${minimized ? styles.windowMinimized : ''}`}
          role="dialog"
          aria-modal="false"
          aria-label={session.title}
          style={{
            left: pos.x,
            top: pos.y,
            width: displayWidth,
          }}
        >
          <div
            className={styles.titlebar}
            onMouseDown={startDrag}
            onDoubleClick={toggleMinimized}
          >
            <div className={styles.titlebarMain}>
              <span className={styles.title}>{session.title}</span>
              <span className={styles.typeBadge}>{session.type}</span>
              <span className={styles.status}>
                <span className={`${styles.statusDot} ${connected ? styles.statusOk : ''}`} />
                {statusLabel}
              </span>
            </div>
            <div className={styles.actions}>
              {!minimized ? (
                <>
                  <Tooltip {...tipProps} title="Отправить Ctrl+Alt+Del">
                    <button
                      type="button"
                      className={styles.iconBtn}
                      aria-label="Ctrl+Alt+Del"
                      onClick={() => guacRef.current?.sendCtrlAltDel()}
                    >
                      <WarningOutlined />
                    </button>
                  </Tooltip>
                  <Tooltip {...tipProps} title="Вставить текст из буфера обмена">
                    <button
                      type="button"
                      className={styles.iconBtn}
                      aria-label="Буфер"
                      onClick={() => void pasteClipboard()}
                    >
                      <SnippetsOutlined />
                    </button>
                  </Tooltip>
                  <Tooltip {...tipProps} title="Переподключить сессию">
                    <button
                      type="button"
                      className={styles.iconBtn}
                      aria-label="Переподключить"
                      onClick={retry}
                    >
                      <ReloadOutlined />
                    </button>
                  </Tooltip>
                  {session.url ? (
                    <Tooltip {...tipProps} title="Открыть в UI Guacamole">
                      <button
                        type="button"
                        className={styles.iconBtn}
                        aria-label="UI Guacamole"
                        onClick={() => window.open(session.url, '_blank', 'noopener,noreferrer')}
                      >
                        <ExportOutlined />
                      </button>
                    </Tooltip>
                  ) : null}
                  <span className={styles.sep} />
                </>
              ) : null}
              <Tooltip {...tipProps} title={minimized ? 'Развернуть' : 'Свернуть'}>
                <button
                  type="button"
                  className={styles.iconBtn}
                  aria-label={minimized ? 'Развернуть' : 'Свернуть'}
                  onClick={toggleMinimized}
                >
                  {minimized ? <BorderOutlined /> : <MinusOutlined />}
                </button>
              </Tooltip>
              <Tooltip {...tipProps} title="Закрыть (Esc)">
                <button
                  type="button"
                  className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                  aria-label="Закрыть"
                  onClick={closeRemote}
                >
                  <CloseOutlined />
                </button>
              </Tooltip>
            </div>
          </div>
          <div
            className={`${styles.stage} ${minimized ? styles.stageHidden : ''}`}
            style={{ height: stageHeight, width: winWidth }}
          >
            <GuacamoleSession
              ref={guacRef}
              key={`${session.id}-${sessionKey}`}
              sessionKey={sessionKey}
              tunnelUrl={session.tunnelUrl}
              connectionToken={session.connectionToken}
              onContentSize={onContentSize}
              onStateChange={(_s, label) => setStatusLabel(label)}
              onError={(msg) => {
                setStatusLabel(msg)
                toast.error(msg)
              }}
              onConnected={() => setStatusLabel('Подключено')}
            />
          </div>
        </div>
      </div>,
      document.body,
    )

  return (
    <RemoteContext.Provider value={value}>
      {children}
      {overlay}
    </RemoteContext.Provider>
  )
}
